import React, { useState, useEffect, useMemo } from 'react';
import {
  Loader2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Home,
  AlertTriangle,
  Info,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from 'recharts';
import { formatCompact } from '../lib/chartFormatters';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { convertAmount } from '../lib/currencyConvert';
import { usePortfolioPrefsStore } from '../store';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { DashboardLayout } from '../components/DashboardLayout';
import { portfolioAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';
import { useForecast } from '../hooks/useForecast';
import { ForecastTotalsFooter } from '../components/dashboard/ForecastTotalsFooter';
import {
  mergeWithForecast,
  splitForecastSeries,
  ForecastCaveat,
  ForecastBarShape,
} from '../lib/forecastChartUtils';

const PLATFORM_LABELS = { uplisting: 'Uplisting', smoobu: 'Smoobu' };
const PLATFORM_COLORS = {
  uplisting: 'bg-blue-100 text-blue-700',
  smoobu: 'bg-purple-100 text-purple-700',
};

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function ChartTooltipContent({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex justify-between gap-4">
          <span style={{ color: entry.color }}>{entry.name}</span>
          <span className="font-mono">{formatCurrency(entry.value, entry.name.includes('FR') ? 'EUR' : 'GBP')}</span>
        </div>
      ))}
    </div>
  );
}

function StackedChartTooltip({ active, payload, label, displayCurrency = 'GBP' }) {
  if (!active || !payload?.length) return null;
  // Access the raw data point from the first payload entry
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-semibold mb-1">{label}</p>
      <div className="flex justify-between gap-4">
        <span className="text-blue-600">UK</span>
        <span className="font-mono">{formatCurrency(point._uk_gbp || 0, 'GBP')}</span>
      </div>
      {point._uk_gbp_margin != null && (
        <div className="flex justify-between gap-4">
          <span className="text-blue-600/60 text-xs ml-2">margin</span>
          <span className="font-mono text-xs text-muted-foreground">{formatCurrency(point._uk_gbp_margin, 'GBP')}</span>
        </div>
      )}
      <div className="flex justify-between gap-4">
        <span className="text-emerald-600">FR</span>
        <span className="font-mono">{formatCurrency(point._fr_eur || 0, 'EUR')}</span>
      </div>
      {point._fr_eur_margin != null && (
        <div className="flex justify-between gap-4">
          <span className="text-emerald-600/60 text-xs ml-2">margin</span>
          <span className="font-mono text-xs text-muted-foreground">{formatCurrency(point._fr_eur_margin, 'EUR')}</span>
        </div>
      )}
      <div className="flex justify-between gap-4 border-t pt-1 mt-1">
        <span className="text-muted-foreground">Combined (approx)</span>
        <span className="font-mono">{formatCurrency(point._combined || 0, displayCurrency)}</span>
      </div>
      {point._combined_margin != null && (
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground text-xs ml-2">margin</span>
          <span className="font-mono text-xs text-muted-foreground">{formatCurrency(point._combined_margin, displayCurrency)}</span>
        </div>
      )}
    </div>
  );
}

function NightlyRateTooltip({ active, payload, label, displayCurrency = 'GBP' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((entry) => (
        entry.value != null && (
          <div key={entry.name} className="flex justify-between gap-4">
            <span style={{ color: entry.color }}>{entry.name}</span>
            <span className="font-mono">{formatCurrency(entry.value, displayCurrency)}/night</span>
          </div>
        )
      ))}
    </div>
  );
}

export function RealEstatePortfolioPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [region, setRegion] = useState('all');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedProperties, setExpandedProperties] = useState(new Set());
  const [sortKey, setSortKey] = useState('revenue');
  const [sortAsc, setSortAsc] = useState(false);

  // Forecast: rolling 90-day forward bookings
  const { forecastByMonth } = useForecast({ scope: 'portfolio', region, year });
  const hasForecast = forecastByMonth && Object.keys(forecastByMonth).length > 0;

  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);
  const eurGbpRate = parseFloat(data?.fx_rates_used?.EUR_GBP) || 0;
  // Series keys for the Combined/cross-region chart only; UK-only and FR-only
  // tabs keep their fixed native-currency keys below (see chart-scope note).
  const combinedUkKey = `UK (${displayCurrency})`;
  const combinedFrKey = `FR (${displayCurrency} approx)`;

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    else setIsLoading(true);

    try {
      const result = await portfolioAPI.get({ region, year });
      setData(result.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [year, region]);

  const toggleProperty = (propertyId) => {
    setExpandedProperties((prev) => {
      const next = new Set(prev);
      next.has(propertyId) ? next.delete(propertyId) : next.add(propertyId);
      return next;
    });
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const sortedProperties = useMemo(() => {
    if (!data?.properties) return [];
    const props = [...data.properties];
    props.sort((a, b) => {
      let av, bv;
      switch (sortKey) {
        case 'name':
          av = a.name.toLowerCase();
          bv = b.name.toLowerCase();
          return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
        case 'revenue':
          av = a.totals.revenue_native || 0;
          bv = b.totals.revenue_native || 0;
          break;
        case 'bookings':
          av = a.totals.bookings || 0;
          bv = b.totals.bookings || 0;
          break;
        case 'nights':
          av = a.totals.nights || 0;
          bv = b.totals.nights || 0;
          break;
        case 'margin':
          av = a.totals.booking_margin_native || 0;
          bv = b.totals.booking_margin_native || 0;
          break;
        default:
          av = a.totals.revenue_native || 0;
          bv = b.totals.revenue_native || 0;
      }
      return sortAsc ? av - bv : bv - av;
    });
    return props;
  }, [data?.properties, sortKey, sortAsc]);

  const chartDataBase = useMemo(() => {
    if (!data?.monthly) return [];
    if (region === 'uk') {
      return data.monthly.map((m) => {
        const parts = m.month.split('-');
        const monthIdx = parseInt(parts[1], 10) - 1;
        return { month: MONTH_NAMES[monthIdx] || m.month, 'UK (GBP)': m.uk_gbp || 0 };
      });
    }
    if (region === 'fr') {
      return data.monthly.map((m) => {
        const parts = m.month.split('-');
        const monthIdx = parseInt(parts[1], 10) - 1;
        return { month: MONTH_NAMES[monthIdx] || m.month, 'FR (EUR)': m.fr_eur || 0 };
      });
    }
    // region === 'all': use combined data.monthly (contains uk_gbp, fr_eur, margins).
    // This is the cross-region view, so it converts to displayCurrency; the
    // uk/fr single-region branches above stay in their own native currency
    // (see chart-scope note near chartCurrency below).
    return data.monthly.map((m) => {
      const parts = m.month.split('-');
      const monthIdx = parseInt(parts[1], 10) - 1;
      const ukConverted = convertAmount(m.uk_gbp || 0, 'GBP', displayCurrency, eurGbpRate);
      const combinedConverted = convertAmount(m.combined_approx_gbp || 0, 'GBP', displayCurrency, eurGbpRate);
      const frConverted = combinedConverted - ukConverted;
      return {
        month: MONTH_NAMES[monthIdx] || m.month,
        [combinedUkKey]: ukConverted,
        [combinedFrKey]: Math.max(0, frConverted),
        _uk_gbp: m.uk_gbp,
        _fr_eur: m.fr_eur,
        _combined: combinedConverted,
        _uk_gbp_margin: m.uk_gbp_margin,
        _fr_eur_margin: m.fr_eur_margin,
        _combined_margin: convertAmount(m.combined_approx_gbp_margin, 'GBP', displayCurrency, eurGbpRate),
      };
    });
  }, [data?.monthly, region, displayCurrency, eurGbpRate, combinedUkKey, combinedFrKey]);

  // Merge forecast into revenue chart data (per-region breakdown from endpoint)
  const chartData = useMemo(() => {
    if (region === 'uk') {
      return mergeWithForecast(chartDataBase, forecastByMonth, (fm) => ({
        'UK (GBP)': fm.revenue_uk_gbp || fm.revenue_gbp || 0,
      }), year);
    }
    if (region === 'fr') {
      return mergeWithForecast(chartDataBase, forecastByMonth, (fm) => ({
        'FR (EUR)': fm.revenue_fr_gbp || fm.revenue_gbp || 0,
      }), year);
    }
    return mergeWithForecast(chartDataBase, forecastByMonth, (fm) => {
      const uk = convertAmount(fm.revenue_uk_gbp || 0, 'GBP', displayCurrency, eurGbpRate);
      const fr = convertAmount(fm.revenue_fr_gbp || 0, 'GBP', displayCurrency, eurGbpRate);
      return { [combinedUkKey]: uk, [combinedFrKey]: fr, _combined: uk + fr };
    }, year);
  }, [chartDataBase, forecastByMonth, region, year, displayCurrency, eurGbpRate, combinedUkKey, combinedFrKey]);

  // UK-only and FR-only tabs show a single region's own bookings and stay in
  // that region's native currency (GBP / EUR respectively), unconverted, same
  // as before this change. Only the "all" (Combined) tab, which mixes both
  // currencies into one approximate total, follows the displayCurrency toggle.
  const chartCurrency = region === 'fr' ? 'EUR' : region === 'uk' ? 'GBP' : displayCurrency;

  const avgNightlyRateDataBase = useMemo(() => {
    if (region !== 'all' || !data?.properties) return [];
    if (eurGbpRate === 0) return [];

    // Derive per-region properties from the single "all" response
    const ukProps = data.properties.filter(p => p.region === 'uk');
    const frProps = data.properties.filter(p => p.region === 'fr');

    // This chart only renders on the "all" tab (a cross-region average), so
    // its values follow displayCurrency the same way the Combined KPI card
    // and Combined revenue chart do.
    const computeMonthlyRate = (properties, nativeCurrency) => {
      const monthMap = {};
      for (const prop of properties) {
        for (const m of prop.monthly || []) {
          if (!monthMap[m.month]) monthMap[m.month] = { revenue: 0, nights: 0 };
          monthMap[m.month].revenue += convertAmount(parseFloat(m.revenue_native) || 0, nativeCurrency, displayCurrency, eurGbpRate);
          monthMap[m.month].nights += m.nights || 0;
        }
      }
      return monthMap;
    };

    const ukMonthly = computeMonthlyRate(ukProps, 'GBP');
    const frMonthly = computeMonthlyRate(frProps, 'EUR');
    const allMonths = [...new Set([...Object.keys(ukMonthly), ...Object.keys(frMonthly)])].sort();

    return allMonths.map((monthKey) => {
      const parts = monthKey.split('-');
      const monthIdx = parseInt(parts[1], 10) - 1;
      const uk = ukMonthly[monthKey];
      const fr = frMonthly[monthKey];
      const totalRev = (uk?.revenue || 0) + (fr?.revenue || 0);
      const totalNights = (uk?.nights || 0) + (fr?.nights || 0);
      return {
        month: MONTH_NAMES[monthIdx] || monthKey,
        'Overall': totalNights > 0 ? totalRev / totalNights : null,
        '\uD83C\uDDEC\uD83C\uDDE7 UK': uk && uk.nights > 0 ? uk.revenue / uk.nights : null,
        '\uD83C\uDDEB\uD83C\uDDF7 France': fr && fr.nights > 0 ? fr.revenue / fr.nights : null,
      };
    });
  }, [region, data?.properties, displayCurrency, eurGbpRate]);

  // Merge forecast into avg nightly rate data (per-region from endpoint).
  // Forecast fields are GBP-denominated (adr_gbp/adr_uk_gbp/adr_fr_gbp), so
  // convert them to displayCurrency too, matching the historical series above.
  const avgNightlyRateDataMerged = useMemo(() => {
    return mergeWithForecast(avgNightlyRateDataBase, forecastByMonth, (fm) => {
      return {
        'Overall': convertAmount(fm.adr_gbp ?? null, 'GBP', displayCurrency, eurGbpRate),
        '\uD83C\uDDEC\uD83C\uDDE7 UK': convertAmount(fm.adr_uk_gbp ?? null, 'GBP', displayCurrency, eurGbpRate),
        '\uD83C\uDDEB\uD83C\uDDF7 France': convertAmount(fm.adr_fr_gbp ?? null, 'GBP', displayCurrency, eurGbpRate),
      };
    }, year);
  }, [avgNightlyRateDataBase, forecastByMonth, year, displayCurrency, eurGbpRate]);

  const avgNightlyRateData = useMemo(
    () => splitForecastSeries(avgNightlyRateDataMerged, ['Overall', '\uD83C\uDDEC\uD83C\uDDE7 UK', '\uD83C\uDDEB\uD83C\uDDF7 France']),
    [avgNightlyRateDataMerged]
  );

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const totals = data?.totals;
  const hasProperties = data?.properties?.length > 0;

  return (
    <DashboardLayout>
      <TooltipProvider>
        <div className="space-y-6 p-6">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                <Home className="h-6 w-6" />
                Real Estate Portfolio
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {(() => {
                  const props = data?.properties || [];
                  const total = props.length;
                  if (region === 'uk') return `${total} propert${total === 1 ? 'y' : 'ies'} in \uD83C\uDDEC\uD83C\uDDE7 UK`;
                  if (region === 'fr') return `${total} propert${total === 1 ? 'y' : 'ies'} in \uD83C\uDDEB\uD83C\uDDF7 France`;
                  const ukCount = props.filter(p => p.region === 'uk').length;
                  const frCount = props.filter(p => p.region === 'fr').length;
                  return `${total} properties across \uD83C\uDDEC\uD83C\uDDE7 UK (${ukCount}) and \uD83C\uDDEB\uD83C\uDDF7 France (${frCount})`;
                })()}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <CurrencyToggle />

              {/* Year Selector */}
              <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-card">
                <button
                  onClick={() => setYear((y) => y - 1)}
                  className="p-1 rounded hover:bg-muted transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="font-semibold w-12 text-center">{year}</span>
                <button
                  onClick={() => setYear((y) => y + 1)}
                  disabled={year >= new Date().getFullYear()}
                  className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Consolidated view link */}
              <Button variant="outline" size="sm" asChild>
                <a href="/portfolio">Try new consolidated view &rarr;</a>
              </Button>

              {/* Refresh */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchData(true)}
                disabled={isRefreshing}
              >
                <RefreshCw className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Region Tabs */}
          <Tabs value={region} onValueChange={setRegion}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="fr">{'\uD83C\uDDEB\uD83C\uDDF7'} France</TabsTrigger>
              <TabsTrigger value="uk">{'\uD83C\uDDEC\uD83C\uDDE7'} UK</TabsTrigger>
            </TabsList>

            <TabsContent value={region}>
              {/* KPI Cards */}
              <div className={cn(
                'grid gap-4 mb-6',
                region === 'all' ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2',
              )}>
                {(region === 'all' || region === 'uk') && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        UK Revenue
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {formatCurrency(totals?.uk?.revenue || 0, 'GBP')}
                      </div>
                      {totals?.uk?.booking_margin != null && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Margin: {formatCurrency(totals.uk.booking_margin, 'GBP')}
                          {totals.uk.revenue > 0 && (
                            <span> ({((totals.uk.booking_margin / totals.uk.revenue) * 100).toFixed(1)}%)</span>
                          )}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {totals?.uk?.bookings || 0} bookings / {totals?.uk?.nights || 0} nights
                      </p>
                    </CardContent>
                  </Card>
                )}

                {(region === 'all' || region === 'fr') && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        FR Revenue
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {formatCurrency(totals?.fr?.revenue || 0, 'EUR')}
                      </div>
                      {totals?.fr?.booking_margin != null && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Margin: {formatCurrency(totals.fr.booking_margin, 'EUR')}
                          {totals.fr.revenue > 0 && (
                            <span> ({((totals.fr.booking_margin / totals.fr.revenue) * 100).toFixed(1)}%)</span>
                          )}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {totals?.fr?.bookings || 0} bookings / {totals?.fr?.nights || 0} nights
                      </p>
                    </CardContent>
                  </Card>
                )}

                {region === 'all' && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                        Combined
                        <UITooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Converted at today's rate. Bookings do not carry captured FX rates.</p>
                          </TooltipContent>
                        </UITooltip>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {totals?.combined_approx_gbp != null
                          ? formatCurrency(convertAmount(totals.combined_approx_gbp, 'GBP', displayCurrency, eurGbpRate), displayCurrency)
                          : 'N/A'}
                      </div>
                      {totals?.combined_approx_gbp_margin != null && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Margin: {formatCurrency(convertAmount(totals.combined_approx_gbp_margin, 'GBP', displayCurrency, eurGbpRate), displayCurrency)}
                          {totals.combined_approx_gbp > 0 && (
                            <span> ({((totals.combined_approx_gbp_margin / totals.combined_approx_gbp) * 100).toFixed(1)}%)</span>
                          )}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {'\uD83C\uDDEC\uD83C\uDDE7'} {formatCurrency(totals?.uk?.revenue || 0, 'GBP')} / {'\uD83C\uDDEB\uD83C\uDDF7'} {formatCurrency(totals?.fr?.revenue || 0, 'EUR')}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        approx GBP
                        {data?.fx_rates_used?.EUR_GBP && (
                          <span> (EUR/GBP: {data.fx_rates_used.EUR_GBP})</span>
                        )}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Data Quality */}
              {(() => {
                const dq = data?.data_quality;
                const hasDq = dq && (
                  (dq.missing_currency_count || 0) > 0 ||
                  (dq.missing_primary_amount_count || 0) > 0 ||
                  (dq.missing_check_in_count || 0) > 0 ||
                  (dq.region_entity_mismatches || 0) > 0 ||
                  (dq.smoobu_unexpected_currency || 0) > 0 ||
                  (dq.bookings_with_no_cost_data || 0) > 0
                );
                if (!hasDq) return null;
                const parts = [];
                if (dq.missing_currency_count > 0)
                  parts.push(`${dq.missing_currency_count} missing currency`);
                if (dq.missing_primary_amount_count > 0)
                  parts.push(`${dq.missing_primary_amount_count} missing primary amount`);
                if (dq.missing_check_in_count > 0)
                  parts.push(`${dq.missing_check_in_count} missing check-in`);
                if (dq.region_entity_mismatches > 0)
                  parts.push(`${dq.region_entity_mismatches} region/entity mismatch`);
                if (dq.smoobu_unexpected_currency > 0)
                  parts.push(`${dq.smoobu_unexpected_currency} unexpected Smoobu currency`);
                if (dq.bookings_with_no_cost_data > 0)
                  parts.push(`${dq.bookings_with_no_cost_data} bookings missing cost data`);
                return (
                  <div className="mb-4 p-3 rounded-lg border border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950 text-sm">
                    <div className="flex items-center gap-2 text-orange-800 dark:text-orange-200">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="font-medium">Data quality:</span> {parts.join(', ')}
                    </div>
                  </div>
                );
              })()}

              {/* Warnings */}
              {data?.warnings?.length > 0 && (
                <div className="mb-4 p-3 rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950 text-sm">
                  <div className="flex items-center gap-2 font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                    <AlertTriangle className="h-4 w-4" />
                    Warnings
                  </div>
                  {data.warnings.map((w, i) => (
                    <p key={i} className="text-yellow-700 dark:text-yellow-300">
                      {typeof w === 'string' ? w : w.message ?? JSON.stringify(w)}
                    </p>
                  ))}
                </div>
              )}

              {/* Monthly Chart */}
              {chartData.length > 0 && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-base">Monthly Revenue</CardTitle>
                    <ForecastCaveat show={hasForecast} />
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={chartData} margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tickFormatter={(v) => {
                            const sym = chartCurrency === 'EUR' ? '\u20AC' : '\u00A3';
                            return `${sym}${(v / 1000).toFixed(0)}k`;
                          }}
                        />

                        {region === 'all' ? (
                          <>
                            <Tooltip content={<StackedChartTooltip data={data?.monthly} displayCurrency={displayCurrency} />} />
                            <Legend />
                            <Bar dataKey={combinedUkKey} stackId="revenue" fill="#3B82F6" radius={[0, 0, 0, 0]} shape={<ForecastBarShape data={chartData} />}>
                              <LabelList
                                dataKey={combinedUkKey}
                                position="center"
                                formatter={(v) => v >= 1000 ? formatCompact(v, displayCurrency) : ''}
                                style={{ fontSize: 11, fontWeight: 600, fill: '#ffffff' }}
                              />
                            </Bar>
                            <Bar dataKey={combinedFrKey} stackId="revenue" fill="#10B981" radius={[4, 4, 0, 0]} shape={<ForecastBarShape data={chartData} />}>
                              <LabelList
                                dataKey={combinedFrKey}
                                position="center"
                                formatter={(v) => v >= 1000 ? formatCompact(v, displayCurrency) : ''}
                                style={{ fontSize: 11, fontWeight: 600, fill: '#ffffff' }}
                              />
                              <LabelList
                                position="top"
                                content={(props) => {
                                  const { x, y, width, index } = props;
                                  const row = chartData[index];
                                  const total = (row?.[combinedUkKey] || 0) + (row?.[combinedFrKey] || 0);
                                  if (!total) return null;
                                  return (
                                    <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill="#1f2937">
                                      {formatCompact(total, displayCurrency)}
                                    </text>
                                  );
                                }}
                              />
                            </Bar>
                          </>
                        ) : region === 'uk' ? (
                          <>
                            <Tooltip content={<ChartTooltipContent />} />
                            <Legend />
                            <Bar dataKey="UK (GBP)" fill="#3B82F6" radius={[4, 4, 0, 0]} shape={<ForecastBarShape data={chartData} />}>
                              <LabelList dataKey="UK (GBP)" position="top" formatter={(v) => formatCompact(v, 'GBP')} style={{ fontSize: 11, fontWeight: 600, fill: '#1f2937' }} />
                            </Bar>
                          </>
                        ) : (
                          <>
                            <Tooltip content={<ChartTooltipContent />} />
                            <Legend />
                            <Bar dataKey="FR (EUR)" fill="#10B981" radius={[4, 4, 0, 0]} shape={<ForecastBarShape data={chartData} />}>
                              <LabelList dataKey="FR (EUR)" position="top" formatter={(v) => formatCompact(v, 'EUR')} style={{ fontSize: 11, fontWeight: 600, fill: '#1f2937' }} />
                            </Bar>
                          </>
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                    <ForecastTotalsFooter
                      data={chartData}
                      series={region === 'all'
                        ? [
                            { key: combinedUkKey, label: 'UK', color: '#3B82F6' },
                            { key: combinedFrKey, label: 'FR', color: '#10B981' },
                          ]
                        : region === 'uk'
                          ? [{ key: 'UK (GBP)', label: 'UK', color: '#3B82F6' }]
                          : [{ key: 'FR (EUR)', label: 'FR', color: '#10B981' }]}
                      currency={region === 'fr' ? 'EUR' : displayCurrency}
                      label="Revenue"
                      showCombined={region === 'all'}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Avg Nightly Rate Chart (All tab only) */}
              {region === 'all' && avgNightlyRateData.length > 0 && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-1">
                      Average Net Nightly Rate
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>All values in GBP approx. FR rates converted at today's EUR/GBP rate.</p>
                        </TooltipContent>
                      </UITooltip>
                    </CardTitle>
                    <ForecastCaveat show={hasForecast} />
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={avgNightlyRateData} margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tickFormatter={(v) => `${displayCurrency === 'EUR' ? '\u20AC' : '\u00A3'}${Math.round(v)}`}
                        />
                        <Tooltip content={<NightlyRateTooltip displayCurrency={displayCurrency} />} />
                        <Legend />

                        <Line type="monotone" dataKey="Overall" stroke="#6B7280" strokeWidth={2.5} dot={{ r: 3 }} connectNulls>
                          <LabelList dataKey="Overall" position="top" formatter={(v) => `${displayCurrency === 'EUR' ? '\u20AC' : '\u00A3'}${Math.round(v)}`} style={{ fontSize: 11, fontWeight: 600, fill: '#6B7280' }} offset={8} />
                        </Line>
                        <Line type="monotone" dataKey="Overall_forecast" stroke="#6B7280" strokeWidth={2.5} strokeDasharray="6 3" dot={false} connectNulls legendType="none" />
                        <Line type="monotone" dataKey={'\uD83C\uDDEC\uD83C\uDDE7 UK'} stroke="#3B82F6" strokeWidth={1.5} dot={false} connectNulls />
                        <Line type="monotone" dataKey={'\uD83C\uDDEC\uD83C\uDDE7 UK_forecast'} stroke="#3B82F6" strokeWidth={1.5} strokeDasharray="6 3" dot={false} connectNulls legendType="none" />
                        <Line type="monotone" dataKey={'\uD83C\uDDEB\uD83C\uDDF7 France'} stroke="#10B981" strokeWidth={1.5} dot={false} connectNulls />
                        <Line type="monotone" dataKey={'\uD83C\uDDEB\uD83C\uDDF7 France_forecast'} stroke="#10B981" strokeWidth={1.5} strokeDasharray="6 3" dot={false} connectNulls legendType="none" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Property Table */}
              {hasProperties ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      Properties ({sortedProperties.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead
                            className="cursor-pointer select-none"
                            onClick={() => handleSort('name')}
                          >
                            Property {sortKey === 'name' && (sortAsc ? '\u2191' : '\u2193')}
                          </TableHead>
                          <TableHead>Platform</TableHead>
                          <TableHead>Entity</TableHead>
                          <TableHead
                            className="text-right cursor-pointer select-none"
                            onClick={() => handleSort('revenue')}
                          >
                            Revenue {sortKey === 'revenue' && (sortAsc ? '\u2191' : '\u2193')}
                          </TableHead>
                          {region === 'all' && (
                            <TableHead className="text-right">GBP (approx)</TableHead>
                          )}
                          <TableHead
                            className="text-right cursor-pointer select-none"
                            onClick={() => handleSort('bookings')}
                          >
                            Bookings {sortKey === 'bookings' && (sortAsc ? '\u2191' : '\u2193')}
                          </TableHead>
                          <TableHead
                            className="text-right cursor-pointer select-none"
                            onClick={() => handleSort('nights')}
                          >
                            Nights {sortKey === 'nights' && (sortAsc ? '\u2191' : '\u2193')}
                          </TableHead>
                          <TableHead className="text-right">Avg/Night</TableHead>
                          <TableHead
                            className="text-right cursor-pointer select-none"
                            onClick={() => handleSort('margin')}
                          >
                            Margin {sortKey === 'margin' && (sortAsc ? '\u2191' : '\u2193')}
                          </TableHead>
                          <TableHead className="text-right">Margin %</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedProperties.map((prop) => {
                          const isExpanded = expandedProperties.has(prop.property_id);
                          const hasBookings = prop.totals.bookings > 0;
                          return (
                            <React.Fragment key={`${prop.platform}-${prop.property_id}`}>
                              <TableRow
                                className={cn(hasBookings && 'cursor-pointer hover:bg-muted/50')}
                                onClick={hasBookings ? () => toggleProperty(prop.property_id) : undefined}
                              >
                                <TableCell className="w-8 px-2">
                                  {hasBookings && (isExpanded
                                    ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                    : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">{prop.name}</TableCell>
                                <TableCell>
                                  <Badge className={cn('text-[10px] px-1.5 py-0 border-0', PLATFORM_COLORS[prop.platform])}>
                                    {PLATFORM_LABELS[prop.platform] || prop.platform}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {prop.entity_name}
                                </TableCell>
                                <TableCell className="text-right font-mono font-semibold text-green-600 dark:text-green-400">
                                  {formatCurrency(prop.totals.revenue_native || 0, prop.currency)}
                                </TableCell>
                                {region === 'all' && (
                                  <TableCell className="text-right font-mono text-muted-foreground">
                                    {prop.totals.revenue_gbp_approx != null
                                      ? formatCurrency(prop.totals.revenue_gbp_approx, 'GBP')
                                      : 'N/A'}
                                  </TableCell>
                                )}
                                <TableCell className="text-right font-mono">
                                  {prop.totals.bookings}
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                  {prop.totals.nights}
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                  {prop.totals.avg_nightly_rate > 0
                                    ? formatCurrency(prop.totals.avg_nightly_rate, prop.currency)
                                    : '\u2014'}
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                  {prop.totals.booking_margin_native != null
                                    ? formatCurrency(prop.totals.booking_margin_native, prop.currency)
                                    : '\u2014'}
                                </TableCell>
                                <TableCell className="text-right font-mono text-muted-foreground">
                                  {prop.totals.margin_pct != null
                                    ? `${prop.totals.margin_pct}%`
                                    : '\u2014'}
                                </TableCell>
                                <TableCell className="w-12">
                                  {prop.totals.quarantined_count > 0 && (
                                    <UITooltip>
                                      <TooltipTrigger asChild>
                                        <Badge variant="destructive" className="text-[10px] px-1 py-0">
                                          <AlertTriangle className="h-3 w-3 mr-0.5" />
                                          {prop.totals.quarantined_count}
                                        </Badge>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{prop.totals.quarantined_count} quarantined booking{prop.totals.quarantined_count !== 1 ? 's' : ''} excluded from revenue</p>
                                      </TooltipContent>
                                    </UITooltip>
                                  )}
                                </TableCell>
                              </TableRow>

                              {/* Expanded property details */}
                              {isExpanded && (
                                <>
                                  {/* Monthly breakdown */}
                                  <TableRow className="bg-muted/20">
                                    <TableCell></TableCell>
                                    <TableCell colSpan={region === 'all' ? 11 : 10}>
                                      <div className="py-2 space-y-3">
                                        {/* Monthly mini-table */}
                                        <div>
                                          <p className="text-xs font-medium text-muted-foreground mb-2">Monthly Breakdown</p>
                                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                                            {prop.monthly.map((m) => {
                                              const parts = m.month.split('-');
                                              const monthIdx = parseInt(parts[1], 10) - 1;
                                              return (
                                                <div key={m.month} className="bg-card rounded p-2 text-xs border">
                                                  <div className="font-medium">{MONTH_NAMES[monthIdx]} {parts[0]}</div>
                                                  <div className="font-mono text-green-600 dark:text-green-400">
                                                    {formatCurrency(m.revenue_native || 0, prop.currency)}
                                                  </div>
                                                  {m.margin_native != null && m.margin_native !== m.revenue_native && (
                                                    <div className="font-mono text-muted-foreground">
                                                      margin {formatCurrency(m.margin_native, prop.currency)}
                                                    </div>
                                                  )}
                                                  <div className="text-muted-foreground">
                                                    {m.bookings} booking{m.bookings !== 1 ? 's' : ''}, {m.nights} night{m.nights !== 1 ? 's' : ''}
                                                  </div>
                                                  {m.avg_nightly_rate_native != null && (
                                                    <div className="text-muted-foreground">
                                                      {formatCurrency(m.avg_nightly_rate_native, prop.currency)}/night
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>

                                        {/* Channel breakdown */}
                                        {Object.keys(prop.channels).length > 0 && (
                                          <div>
                                            <p className="text-xs font-medium text-muted-foreground mb-2">Channel Breakdown</p>
                                            <div className="flex flex-wrap gap-2">
                                              {Object.entries(prop.channels)
                                                .sort(([, a], [, b]) => b - a)
                                                .map(([channel, revenue]) => (
                                                  <Badge key={channel} variant="secondary" className="text-xs font-mono">
                                                    {channel}: {formatCurrency(revenue, prop.currency)}
                                                  </Badge>
                                                ))}
                                            </div>
                                          </div>
                                        )}

                                      </div>
                                    </TableCell>
                                  </TableRow>
                                </>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                    <Home className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-1">No properties found for this region</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Properties are synced from Uplisting and Smoobu integrations.
                    </p>
                    <Button variant="outline" asChild>
                      <a href="/integrations">Go to Integrations</a>
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </TooltipProvider>
    </DashboardLayout>
  );
}
